/**
 * Chat interno: conversas diretas, grupos, canais, conversa do processo/caso e o assistente (consultas prontas, sem IA).
 * Em qualquer conversa: "/" ou "@bot" pergunta ao sistema (só você vê a resposta).
 */
import { SortTh, sortKeyOf, sortRows, type SortState } from "@/lib/sort";
import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { navigate, useSearch, useTitle } from "@/lib/router";
import { useLookups } from "@/lib/catalog";
import { Empty, ErrorBox, Field, Loading, Modal, SearchBox } from "@/ui/kit";
import { Avatar, Icon } from "@/ui/icons";
import { confirmar, toast } from "@/ui/feedback";
import { NewActivityModal } from "./ActivityPages";
import type { EntityRef } from "@/ui/kit";
import type { QueryResult } from "@/ui/home-widgets";
import { onLive } from "@/lib/live";

export interface Conv { id: string; kind: "dm" | "grupo" | "canal" | "processo" | "caso" | "bot"; title: string; unread: number; lastAt: string | null; last: { body: string; who: string } | null; otherUserId: string | null; processId: string | null; caseId: string | null; members?: string[] }
interface Att { id: string; name: string; mimeType: string; size: number }
interface BotPayload { type: "help" | "result" | "mfa" | "erro"; result?: QueryResult; alt?: Array<{ key: string; label: string; params: Record<string, unknown> }>; options?: Array<{ key: string; label: string; group: string; needsQ?: string; periodos?: string[] }>; key?: string; params?: Record<string, unknown>; msg?: string }
interface Msg { reactions?: Record<string, string[]> | null; id: string; userId: string | null; body: string; parentId: string | null; mentions: string[]; attachments: Att[] | null; botFor: string | null; botPayload: BotPayload | null; activityId: string | null; noteId: string | null; editedAt: string | null; deletedAt: string | null; createdAt: string; replies: number; canEdit: boolean }
interface MsgPage { seen?: Record<string, string>; conversation: { id: string; kind: Conv["kind"]; title: string; processId: string | null; caseId: string | null; members?: string[] }; messages: Msg[]; hasMore: boolean }

const GROUPS: Array<[string, Array<Conv["kind"]>]> = [["Assistente", ["bot"]], ["Conversas", ["dm"]], ["Grupos", ["grupo"]], ["Canais", ["canal"]], ["Processos e casos", ["processo", "caso"]]];
const hhmm = (iso: string) => {
  const d = new Date(iso); const today = new Date().toDateString() === d.toDateString();
  return today ? d.toLocaleTimeString("pt-BR", { hour: "2-digit", minute: "2-digit" }) : d.toLocaleString("pt-BR", { day: "2-digit", month: "2-digit", hour: "2-digit", minute: "2-digit" });
};

export function ChatPage(): JSX.Element {
  useTitle("Chat");
  const [search, setSearch] = useSearch();
  const { data: convs, error } = useQuery({ queryKey: ["chat-conversas"], queryFn: () => api.get<Conv[]>("/chat/conversas"), refetchInterval: 60_000 });
  const [q, setQ] = useState("");
  const [nova, setNova] = useState(false);
  const current = search.get("c") ?? convs?.find((c) => c.kind === "bot")?.id ?? null;
  const list = (convs ?? []).filter((c) => !q || c.title.toLowerCase().includes(q.toLowerCase()));
  return (
    <div className="chat-layout">
      <aside className="chat-side card">
        <div className="row nowrap" style={{ marginBottom: 8 }}>
          <SearchBox value={q} onChange={setQ} placeholder="Buscar conversa…" />
          <button className="btn small primary" title="Nova conversa ou grupo" onClick={() => setNova(true)}><Icon name="plus" size="sm" /></button>
        </div>
        <ErrorBox error={error} />
        {!convs ? <Loading /> : GROUPS.map(([label, kinds]) => {
          const items = list.filter((c) => kinds.includes(c.kind));
          if (!items.length) return null;
          return (
            <div key={label}>
              <div className="nav-section" style={{ color: "var(--muted)" }}>{label}</div>
              {items.map((c) => (
                <button key={c.id} className={`chat-conv ${c.id === current ? "on" : ""}`} onClick={() => setSearch({ c: c.id, m: null })}>
                  {c.kind === "dm" && c.otherUserId ? <Avatar name={c.title} id={c.otherUserId} size={28} /> : <span className="chat-ic"><Icon name={c.kind === "bot" ? "bot" : c.kind === "processo" ? "scale" : c.kind === "caso" ? "folder" : c.kind === "canal" ? "flag" : "chat"} size="sm" /></span>}
                  <span className="grow" style={{ minWidth: 0 }}>
                    <b className="ell">{c.title}</b>
                    {c.last && <small className="ell muted">{c.last.who}: {c.last.body}</small>}
                  </span>
                  {c.unread > 0 && <span className="nav-badge">{c.unread}</span>}
                </button>
              ))}
            </div>
          );
        })}
      </aside>
      <section className="chat-main card">
        {current ? <ConversationView key={current} id={current} focusMsg={search.get("m")} /> : <Empty icon="chat">Escolha uma conversa.</Empty>}
      </section>
      {nova && <NewConvModal onClose={() => setNova(false)} onCreated={(id) => { setNova(false); setSearch({ c: id }); }} />}
    </div>
  );
}

// ------------------------------------------------------------ conversa (também usada dentro do processo/caso)

export function RecordChat({ kind, id }: { kind: "processo" | "caso"; id: string }): JSX.Element {
  const { data, error } = useQuery({ queryKey: ["chat-record", kind, id], queryFn: () => api.get<{ id: string }>(`/chat/${kind}/${id}`) });
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  return <div className="card chat-embed"><ConversationView id={data.id} compact /></div>;
}

export function ConversationView({ id, compact, focusMsg, dock }: { id: string; compact?: boolean; focusMsg?: string | null; dock?: boolean }): JSX.Element {
  const qc = useQueryClient();
  const { me, userName } = useLookups();
  const { data, error } = useQuery({ queryKey: ["chat-msgs", id, null], queryFn: () => api.get<MsgPage>(`/chat/conversas/${id}/mensagens`), refetchInterval: 120_000 });
  const [older, setOlder] = useState<Msg[]>([]);
  const [thread, setThread] = useState<Msg | null>(null);
  const [actFor, setActFor] = useState<Msg | null>(null);
  const box = useRef<HTMLDivElement>(null);
  const [typing, setTyping] = useState<string | null>(null);
  useEffect(() => {
    let t: ReturnType<typeof setTimeout> | null = null;
    const off = onLive((e) => {
      if (e.c !== id || !e.by || e.by === me?.id) return;
      if (e.t === "typing") { setTyping(e.by); if (t) clearTimeout(t); t = setTimeout(() => setTyping(null), 5000); }
      if (e.t === "msg") setTyping(null);
    });
    return () => { off(); if (t) clearTimeout(t); };
  }, [id, me?.id]);
  const msgs = useMemo(() => [...older, ...(data?.messages ?? [])], [older, data]);
  const lastId = data?.messages[data.messages.length - 1]?.id;
  useEffect(() => { if (!focusMsg && box.current) box.current.scrollTop = box.current.scrollHeight; }, [lastId, focusMsg]);
  useEffect(() => { void qc.invalidateQueries({ queryKey: ["chat-conversas"] }); void qc.invalidateQueries({ queryKey: ["chat-resumo"] }); }, [lastId, qc]);
  useEffect(() => { if (focusMsg) document.getElementById(`m-${focusMsg}`)?.scrollIntoView({ block: "center" }); }, [focusMsg, data]);
  const loadOlder = async () => {
    const first = msgs[0]; if (!first) return;
    const r = await api.get<MsgPage>(`/chat/conversas/${id}/mensagens?antes=${encodeURIComponent(first.createdAt)}`);
    setOlder([...r.messages, ...older]);
  };
  if (error) return <ErrorBox error={error} />;
  if (!data) return <Loading />;
  const c = data.conversation;
  const refresh = () => qc.invalidateQueries({ queryKey: ["chat-msgs", id] });
  return (
    <div className={`chat-conv-view ${compact ? "compact" : ""}`}>
      {!compact && !dock && (
        <div className="chat-head">
          <b>{c.title}</b>
          {c.processId && <button className="link-btn small" onClick={() => navigate(`/processos/${c.processId}`)}>abrir processo →</button>}
          {c.caseId && <button className="link-btn small" onClick={() => navigate(`/casos/${c.caseId}`)}>abrir caso →</button>}
          {c.members && c.kind === "grupo" && <span className="muted small">{c.members.map((u) => userName(u).split(" ")[0]).join(", ")}</span>}
          {c.kind === "dm" && <span className="muted small">🔒 conversa privada — só vocês dois leem</span>}
          {c.kind === "bot" && <span className="muted small">Consultas prontas, com as suas permissões. Só você vê.</span>}
        </div>
      )}
      <div className="chat-msgs" ref={box}>
        {data.hasMore && !older.length && <button className="link-btn small" onClick={() => void loadOlder()}>carregar anteriores</button>}
        {!msgs.length && <div className="muted small pad">{c.kind === "bot" ? "Pergunte algo ou escolha uma consulta abaixo." : c.kind === "processo" || c.kind === "caso" ? "Ainda sem conversa sobre este registro. Quem abrir daqui a meses vai ver o que foi combinado aqui." : "Nenhuma mensagem ainda."}</div>}
        {msgs.map((m, i) => {
          const prev = msgs[i - 1];
          const first = !prev || prev.userId !== m.userId || new Date(m.createdAt).getTime() - new Date(prev.createdAt).getTime() > 5 * 60_000 || !!prev.botPayload;
          const lastMine = m.userId === me?.id && !msgs.slice(i + 1).some((x) => x.userId === me?.id);
          const seenBy = lastMine && data.seen ? Object.entries(data.seen).filter(([, at]) => at >= m.createdAt).map(([u]) => u) : [];
          return <MessageRow key={m.id} m={m} convId={id} me={me?.id} first={first} showName={c.kind !== "dm" && c.kind !== "bot"} seenBy={seenBy} isDm={c.kind === "dm"} onThread={setThread} onActivity={setActFor} onChanged={refresh} />;
        })}
        {typing && <div className="chat-typing">{userName(typing).split(" ")[0]} está digitando<span className="dots"><i /><i /><i /></span></div>}
        {c.kind === "bot" && !msgs.length && <BotHelp convId={id} />}
      </div>
      <Composer convId={id} parentId={null} typingPing={c.kind === "dm" || c.kind === "grupo"} placeholder={dock && c.kind !== "bot" ? "Mensagem…" : c.kind === "bot" ? "Pergunte: inadimplentes, prazos da semana, status do processo 0001234-…" : "Mensagem… (@ para mencionar, / para perguntar ao sistema)"} />
      {thread && (
        <Modal title="Tópico" onClose={() => setThread(null)} wide>
          <ThreadView convId={id} parent={thread} onActivity={setActFor} />
        </Modal>
      )}
      {actFor && (
        <NewActivityModal
          defaults={{ ctx: c.processId ? { kind: "processo", id: c.processId, label: c.title } as EntityRef : c.caseId ? { kind: "caso", id: c.caseId, label: c.title } as EntityRef : undefined, title: actFor.body.split("\n")[0]!.slice(0, 200), description: `${actFor.body}\n\n— ${actFor.userId ? userName(actFor.userId) : "Sistema"}, no chat (${c.title})` }}
          onClose={() => setActFor(null)}
          onCreated={async (aid) => { await api.post(`/chat/mensagens/${actFor.id}/atividade`, { activityId: aid }); toast.ok("Atividade criada"); void refresh(); }} />
      )}
    </div>
  );
}

function ThreadView({ convId, parent, onActivity }: { convId: string; parent: Msg; onActivity: (m: Msg) => void }): JSX.Element {
  const qc = useQueryClient();
  const { me } = useLookups();
  const { data } = useQuery({ queryKey: ["chat-msgs", convId, parent.id], queryFn: () => api.get<MsgPage>(`/chat/conversas/${convId}/mensagens?topico=${parent.id}`), refetchInterval: 30_000 });
  return (
    <div className="chat-conv-view compact">
      <div className="chat-msgs" style={{ maxHeight: 420 }}>
        <MessageRow m={parent} convId={convId} me={me?.id} onActivity={onActivity} onChanged={() => qc.invalidateQueries({ queryKey: ["chat-msgs", convId] })} />
        <hr className="sep" />
        {(data?.messages ?? []).map((m) => <MessageRow key={m.id} m={m} convId={convId} me={me?.id} onActivity={onActivity} onChanged={() => qc.invalidateQueries({ queryKey: ["chat-msgs", convId] })} />)}
      </div>
      <Composer convId={convId} parentId={parent.id} placeholder="Responder no tópico…" />
    </div>
  );
}

function renderBody(body: string, mentions: string[], userName: (id: string) => string): JSX.Element {
  if (!mentions.length) return <>{body}</>;
  const names = mentions.map((u) => `@${userName(u)}`).sort((a, b) => b.length - a.length);
  const parts: Array<string | JSX.Element> = [];
  let rest = body;
  let k = 0;
  while (rest) {
    let best = -1, name = "";
    for (const n of names) { const i = rest.indexOf(n); if (i >= 0 && (best < 0 || i < best)) { best = i; name = n; } }
    if (best < 0) { parts.push(rest); break; }
    parts.push(rest.slice(0, best), <span key={k++} className="mention">{name}</span>);
    rest = rest.slice(best + name.length);
  }
  return <>{parts}</>;
}

const EMOJIS = ["👍", "✅", "👀", "❤️", "😂", "🙏"];

function MessageRow({ m, convId, me, first = true, showName = true, seenBy = [], isDm, onThread, onActivity, onChanged }: {
  m: Msg; convId: string; me?: string; first?: boolean; showName?: boolean; seenBy?: string[]; isDm?: boolean;
  onThread?: (m: Msg) => void; onActivity: (m: Msg) => void; onChanged: () => void;
}): JSX.Element {
  const { userName } = useLookups();
  const [editing, setEditing] = useState<string | null>(null);
  const [picker, setPicker] = useState(false);
  const mine = m.userId === me;
  const toNote = async () => { try { await api.post(`/chat/mensagens/${m.id}/postit`, {}); toast.ok("Post-it criado no seu mural"); onChanged(); } catch (e) { toast.error((e as Error).message); } };
  const save = async () => { try { await api.patch(`/chat/mensagens/${m.id}`, { body: editing }); setEditing(null); onChanged(); } catch (e) { toast.error((e as Error).message); } };
  const del = async () => { if (await confirmar("Apagar esta mensagem? Fica registrado que foi apagada.", { confirm: "Apagar", danger: true })) { try { await api.del(`/chat/mensagens/${m.id}`); onChanged(); } catch (e) { toast.error((e as Error).message); } } };
  const react = async (emoji: string) => { setPicker(false); try { await api.post(`/chat/mensagens/${m.id}/reacao`, { emoji }); onChanged(); } catch (e) { toast.error((e as Error).message); } };
  const bot = !m.userId;
  const reactions = Object.entries(m.reactions ?? {}).filter(([, us]) => us.length);
  return (
    <div id={`m-${m.id}`} className={`bub-row ${mine ? "mine" : "theirs"} ${bot ? "bot" : ""} ${first ? "first" : ""} ${m.botFor ? "ephemeral" : ""}`}>
      {!mine && <div className="bub-av">{first ? (m.userId ? <Avatar name={userName(m.userId)} id={m.userId} size={28} /> : <span className="chat-ic bot"><Icon name="bot" size="sm" /></span>) : null}</div>}
      <div className="bub-col">
        {first && !mine && (showName || bot) && <div className="bub-name">{m.userId ? userName(m.userId) : "Assistente"}{m.botFor ? <span className="muted"> · só você vê</span> : null}</div>}
        <div className={`bub ${bot && m.botPayload ? "wide" : ""}`}>
          {m.deletedAt ? <i className="muted">mensagem apagada</i>
            : editing !== null ? (
              <div className="stack" style={{ gap: 4 }}><textarea className="input" rows={2} value={editing} onChange={(e) => setEditing(e.target.value)} autoFocus /><div className="row"><button className="btn small primary" onClick={() => void save()}>Salvar</button><button className="btn small" onClick={() => setEditing(null)}>Cancelar</button></div></div>
            ) : m.botPayload ? <BotAnswer p={m.botPayload} convId={convId} /> : <span className="chat-body">{renderBody(m.body, m.mentions, userName)}</span>}
          {!m.deletedAt && m.attachments?.map((a) => (
            <button key={a.id} className="chat-att" onClick={() => void api.download(`/documentos/${a.id}/download`, a.name, /^image\/|pdf/.test(a.mimeType))}>📎 {a.name}</button>
          ))}
          <span className="bub-time">{m.editedAt ? "editada · " : ""}{hhmm(m.createdAt)}</span>
        </div>
        {reactions.length > 0 && (
          <div className="bub-reacts">{reactions.map(([e, us]) => <button key={e} className={us.includes(me ?? "") ? "on" : ""} title={us.map((u) => userName(u).split(" ")[0]).join(", ")} onClick={() => void react(e)}>{e} {us.length}</button>)}</div>
        )}
        <div className="bub-links">
          {m.replies > 0 && onThread && <button className="link-btn small" onClick={() => onThread(m)}>{m.replies} resposta(s)</button>}
          {m.activityId && <button className="link-btn small" onClick={() => navigate(`/atividades/${m.activityId}`)}>virou atividade</button>}
          {m.noteId && <button className="link-btn small" onClick={() => navigate(`/mural?nota=${m.noteId}`)}>virou post-it</button>}
          {seenBy.length > 0 && <span className="bub-seen">✓✓ {isDm ? "visto" : `visto por ${seenBy.map((u) => userName(u).split(" ")[0]).join(", ")}`}</span>}
        </div>
      </div>
      {!m.deletedAt && m.userId && (
        <div className="chat-actions">
          <button title="Reagir" onClick={() => setPicker(!picker)}>😊</button>
          {onThread && !m.parentId && <button title="Responder no tópico" onClick={() => onThread(m)}>↩︎</button>}
          {!m.activityId && <button title="Criar atividade" onClick={() => onActivity(m)}>✅</button>}
          {!m.noteId && <button title="Criar post-it" onClick={() => void toNote()}>📌</button>}
          {m.canEdit && <button title="Editar (até 15 min)" onClick={() => setEditing(m.body)}>✎</button>}
          {m.canEdit && <button title="Apagar (até 15 min)" onClick={() => void del()}>🗑</button>}
          {picker && <div className="react-picker">{EMOJIS.map((e) => <button key={e} onClick={() => void react(e)}>{e}</button>)}</div>}
        </div>
      )}
    </div>
  );
}

// ------------------------------------------------------------ assistente (bot)

function BotHelp({ convId }: { convId: string }): JSX.Element {
  const { data } = useQuery({ queryKey: ["chat-bot-opcoes"], queryFn: () => api.get<NonNullable<BotPayload["options"]>>("/chat/bot/consultas"), staleTime: 300_000 });
  return data ? <BotAnswer p={{ type: "help", options: data }} convId={convId} /> : <Loading />;
}

function BotAnswer({ p, convId }: { p: BotPayload; convId: string }): JSX.Element {
  const qc = useQueryClient();
  const [code, setCode] = useState("");
  const [ask, setAsk] = useState<{ key: string; needsQ: string; label: string } | null>(null);
  const [qText, setQText] = useState("");
  const run = async (key: string, params: Record<string, unknown> = {}, mfa?: string) => {
    try { await api.post(`/chat/conversas/${convId}/bot`, { key, params, ...(mfa ? { code: mfa } : {}) }); await qc.invalidateQueries({ queryKey: ["chat-msgs", convId] }); }
    catch (e) { toast.error((e as Error).message); }
  };
  if (p.type === "help") {
    const groups = [...new Set((p.options ?? []).map((o) => o.group))];
    return (
      <div className="bot-help">
        {p.msg && <div className="small" style={{ marginBottom: 6 }}>{p.msg}</div>}
        {groups.map((g) => (
          <div key={g} style={{ marginBottom: 6 }}>
            <div className="muted small">{g}</div>
            <div className="chips">{(p.options ?? []).filter((o) => o.group === g).map((o) => (
              <button key={o.key} className="chip" onClick={() => (o.needsQ ? setAsk({ key: o.key, needsQ: o.needsQ, label: o.label }) : void run(o.key))}>{o.label}</button>
            ))}</div>
          </div>
        ))}
        {ask && (
          <div className="row nowrap" style={{ marginTop: 6 }}>
            <input className="input" autoFocus placeholder={ask.needsQ === "processo" ? "Número CNJ ou nome da parte" : "Nome ou CPF/CNPJ do cliente"} value={qText} onChange={(e) => setQText(e.target.value)} onKeyDown={(e) => { if (e.key === "Enter" && qText.trim()) { void run(ask.key, { q: qText.trim() }); setAsk(null); setQText(""); } }} />
            <button className="btn small primary" disabled={!qText.trim()} onClick={() => { void run(ask.key, { q: qText.trim() }); setAsk(null); setQText(""); }}>{ask.label}</button>
          </div>
        )}
      </div>
    );
  }
  if (p.type === "mfa") {
    return (
      <div className="bot-help">
        <div className="small">🔐 {p.msg}</div>
        <div className="row nowrap" style={{ marginTop: 6 }}>
          <input className="input" style={{ width: 140 }} inputMode="numeric" maxLength={6} placeholder="000000" value={code} onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))} onKeyDown={(e) => { if (e.key === "Enter" && code.length === 6) void run(p.key!, p.params, code); }} />
          <button className="btn small primary" disabled={code.length !== 6} onClick={() => void run(p.key!, p.params, code)}>Confirmar</button>
        </div>
      </div>
    );
  }
  if (p.type === "erro") return <div className="error-box small">{p.msg}</div>;
  const r = p.result!;
  const exportXlsx = () => api.downloadPost("/bi/exportar", { title: r.title.slice(0, 120), sheets: [{ name: "Consulta", columns: r.columns.map((c, i) => c || (i ? "Valor" : "Campo")), rows: r.rows }] }, `${r.title.replace(/[^\w\- ]+/g, "").slice(0, 60) || "consulta"}.xlsx`);
  return (
    <div className="bot-result">
      <div><b>{r.title}</b>{r.summary && <span className="muted small"> — {r.summary}</span>}</div>
      {r.choices?.length ? (
        <div className="chips" style={{ marginTop: 6 }}><span className="muted small">{r.empty}</span>{r.choices.map((ch) => <button key={ch.q} className="chip" onClick={() => void run(r.key, { ...(r as unknown as { params?: object }).params, q: ch.q })}>{ch.label}</button>)}</div>
      ) : r.rows.length ? (
        <div className="table-wrap" style={{ marginTop: 6, maxHeight: 360, overflow: "auto" }}>
          <ResultTable columns={r.columns} rows={r.rows} links={r.links} />
        </div>
      ) : <div className="muted small" style={{ marginTop: 4 }}>{r.empty}</div>}
      <div className="row small" style={{ marginTop: 6, gap: 8 }}>
        {r.more && <button className="link-btn small" onClick={() => navigate(r.more!)}>Abrir no sistema →</button>}
        {r.rows.length > 0 && !r.choices?.length && <button className="link-btn small" onClick={() => void exportXlsx()}>Baixar Excel</button>}
        {(p.alt ?? []).map((a) => <button key={a.key} className="chip" onClick={() => void run(a.key, a.params)}>{a.label}?</button>)}
      </div>
    </div>
  );
}

// ------------------------------------------------------------ escrever

/** Tabela de resultado das consultas, com ordenação pela coluna (quando há títulos de coluna). */
function ResultTable({ columns, rows, links }: { columns: Array<string | null | undefined>; rows: Array<Array<string | number | null | undefined>>; links: Array<string | null | undefined> }): JSX.Element {
  const [sort, setSort] = useState<SortState>({ key: null, dir: null });
  const items = rows.map((row, i) => ({ row, link: links[i] }));
  const j = sort.key !== null ? Number(sort.key) : -1;
  const view = j >= 0 && sort.dir ? sortRows(items, (x) => sortKeyOf(x.row[j] ?? null), sort.dir) : items;
  const hasHead = columns.some((c) => c);
  return (
    <table className="table">
      {hasHead && <thead><tr>{columns.map((c, i) => <SortTh key={i} label={c} k={String(i)} sort={sort} onSort={setSort} />)}</tr></thead>}
      <tbody>{view.map((x, i) => (
        <tr key={i} className={x.link ? "clickable" : ""} onClick={() => x.link && navigate(x.link)}>
          {x.row.map((cell, k) => <td key={k} style={k === 0 && !columns[0] ? { fontWeight: 600, whiteSpace: "nowrap" } : undefined}>{cell ?? ""}</td>)}
        </tr>
      ))}</tbody>
    </table>
  );
}

function Composer({ convId, parentId, placeholder, typingPing }: { convId: string; parentId: string | null; placeholder: string; typingPing?: boolean }): JSX.Element {
  const lastPing = useRef(0);
  const qc = useQueryClient();
  const { catalog, me } = useLookups();
  const [text, setText] = useState("");
  const [mentions, setMentions] = useState<Array<{ id: string; name: string }>>([]);
  const [atts, setAtts] = useState<Att[]>([]);
  const [busy, setBusy] = useState(false);
  const [mq, setMq] = useState<string | null>(null);
  const ta = useRef<HTMLTextAreaElement>(null);
  const users = (catalog?.users ?? []).filter((u) => u.id !== me?.id);
  const sugg = mq !== null ? users.filter((u) => u.displayName.toLowerCase().includes(mq.toLowerCase())).slice(0, 6) : [];
  const onChange = (v: string) => {
    setText(v);
    if (typingPing && v && Date.now() - lastPing.current > 3000) { lastPing.current = Date.now(); void api.post(`/chat/conversas/${convId}/digitando`).catch(() => undefined); }
    const caret = ta.current?.selectionStart ?? v.length;
    const m = /(^|\s)@([\p{L}\s]{0,30})$/u.exec(v.slice(0, caret));
    setMq(m && !m[2]!.includes("  ") ? m[2]! : null);
  };
  const pick = (u: { id: string; displayName: string }) => {
    const caret = ta.current?.selectionStart ?? text.length;
    const before = text.slice(0, caret).replace(/@([\p{L}\s]{0,30})$/u, `@${u.displayName} `);
    setText(before + text.slice(caret));
    setMentions([...mentions.filter((x) => x.id !== u.id), { id: u.id, name: u.displayName }]);
    setMq(null);
    ta.current?.focus();
  };
  const send = async () => {
    if (busy || (!text.trim() && !atts.length)) return;
    setBusy(true);
    try {
      await api.post(`/chat/conversas/${convId}/mensagens`, { body: text, parentId, mentions: mentions.filter((m) => text.includes(`@${m.name}`)).map((m) => m.id), attachments: atts });
      setText(""); setMentions([]); setAtts([]);
      await qc.invalidateQueries({ queryKey: ["chat-msgs", convId] });
    } catch (e) { toast.error((e as Error).message); } finally { setBusy(false); }
  };
  const upload = async (file: File) => {
    const form = new FormData(); form.append("file", file);
    try { const d = await api.upload<Att>(`/chat/conversas/${convId}/anexo`, form); setAtts((a) => [...a, d]); }
    catch (e) { toast.error((e as Error).message); }
  };
  return (
    <div className="chat-composer">
      {sugg.length > 0 && (
        <div className="mention-pop">{sugg.map((u) => <button key={u.id} onClick={() => pick(u)}><Avatar name={u.displayName} id={u.id} size={20} /> {u.displayName}</button>)}</div>
      )}
      {atts.length > 0 && <div className="row small" style={{ marginBottom: 4 }}>{atts.map((a) => <span key={a.id} className="chip">📎 {a.name} <a onClick={() => setAtts(atts.filter((x) => x.id !== a.id))}>✕</a></span>)}</div>}
      <div className="row nowrap" style={{ alignItems: "flex-end" }}>
        <label className="btn small" title="Anexar arquivo (vai para Documentos)">📎<input type="file" hidden onChange={(e) => { const f = e.target.files?.[0]; if (f) void upload(f); e.target.value = ""; }} /></label>
        <textarea ref={ta} className="input" rows={1} placeholder={placeholder} value={text} onChange={(e) => onChange(e.target.value)}
          onKeyDown={(e) => {
            if (sugg.length && (e.key === "Enter" || e.key === "Tab")) { e.preventDefault(); pick(sugg[0]!); return; }
            if (e.key === "Escape") setMq(null);
            if (e.key === "Enter" && !e.shiftKey) { e.preventDefault(); void send(); }
          }} style={{ resize: "vertical", minHeight: 38 }} />
        <button className="btn primary" disabled={busy || (!text.trim() && !atts.length)} onClick={() => void send()}>Enviar</button>
      </div>
    </div>
  );
}

// ------------------------------------------------------------ nova conversa

export function NewConvModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }): JSX.Element {
  const { catalog, me, can } = useLookups();
  const [kind, setKind] = useState<"dm" | "grupo" | "canal">("dm");
  const [sel, setSel] = useState<string[]>([]);
  const [title, setTitle] = useState("");
  const [q, setQ] = useState("");
  const [err, setErr] = useState<unknown>(null);
  const users = (catalog?.users ?? []).filter((u) => u.id !== me?.id && u.displayName.toLowerCase().includes(q.toLowerCase()));
  const create = async (ids = sel) => {
    try { const c = await api.post<{ id: string }>("/chat/conversas", { kind, userIds: ids, title: title || undefined }); onCreated(c.id); }
    catch (e) { setErr(e); }
  };
  return (
    <Modal title="Nova conversa" onClose={onClose} footer={kind !== "dm" ? <><button className="btn" onClick={onClose}>Cancelar</button><button className="btn primary" disabled={kind === "grupo" ? !sel.length || !title.trim() : !title.trim()} onClick={() => void create()}>Criar</button></> : undefined}>
      <div className="row" style={{ marginBottom: 10 }}>
        <button className={`btn small ${kind === "dm" ? "primary" : ""}`} onClick={() => setKind("dm")}>Com uma pessoa</button>
        <button className={`btn small ${kind === "grupo" ? "primary" : ""}`} onClick={() => setKind("grupo")}>Grupo</button>
        {can("config.gerenciar") && <button className={`btn small ${kind === "canal" ? "primary" : ""}`} onClick={() => setKind("canal")}>Canal (todos)</button>}
      </div>
      {kind !== "dm" && <Field label={kind === "grupo" ? "Nome do grupo" : "Nome do canal"}><input className="input" value={title} maxLength={120} onChange={(e) => setTitle(e.target.value)} /></Field>}
      {kind !== "canal" && (
        <>
          <SearchBox value={q} onChange={setQ} placeholder="Buscar pessoa…" />
          <div className="member-list" style={{ marginTop: 8 }}>
            {users.map((u) => kind === "dm"
              ? <button key={u.id} className="chat-conv" onClick={() => void create([u.id])}><Avatar name={u.displayName} id={u.id} size={26} /><span className="grow">{u.displayName}</span></button>
              : <label key={u.id} className="row nowrap"><input type="checkbox" checked={sel.includes(u.id)} onChange={(e) => setSel(e.target.checked ? [...sel, u.id] : sel.filter((x) => x !== u.id))} /><Avatar name={u.displayName} id={u.id} size={22} /> {u.displayName}</label>)}
          </div>
        </>
      )}
      <ErrorBox error={err} />
    </Modal>
  );
}
